import { fromDirectus } from "../document/fromDirectus";
import type { CVDocument } from "../document/model";
import { toCVData } from "../document/toCVData";
import type { RespuestaSugerencias, Sugerencia } from "../suggestions/types";
import { ErrorApi } from "./errores";
import type { CvTransporte } from "./transporte";

/**
 * El cliente HTTP del editor.
 *
 * Reemplaza `src/cases/` entero: 72 archivos que implementan
 * usecase -> impl -> manager -> controller -> store sobre urunner-lib para,
 * al final, hacer un fetch. Aqui son cuatro funciones y un tipo de error.
 */

const PROD = "https://api.almia.com.co/";
const STAGING = "https://staging.api.almia.com.co/";

/**
 * A que Directus habla el editor.
 *
 * DESARROLLO NUNCA CAE A PRODUCCION. La version anterior copiaba la logica de
 * `src/constants/index.ts` -- explicito, luego staging si el host lo dice,
 * luego produccion -- y en local eso resolvia a PRODUCCION: "localhost" no
 * contiene "staging", asi que ganaba el ultimo caso. El editor cargaba un CV
 * real de produccion y el autoguardado habria escrito encima.
 *
 * Un default que apunta a produccion desde una maquina de desarrollo no es un
 * default, es una trampa. Aqui localhost va a staging salvo que se diga otra
 * cosa a proposito.
 */
const resolverApi = (): string => {
  const explicito = import.meta.env.VITE_API_URL as string | undefined;
  if (explicito) return explicito.endsWith("/") ? explicito : `${explicito}/`;

  if (typeof window === "undefined") return STAGING;

  const host = window.location.hostname.toLowerCase();
  const enLocal = host === "localhost" || host === "127.0.0.1" || host.endsWith(".local");
  if (enLocal || host.includes("staging")) return STAGING;

  return PROD;
};

export const API = resolverApi();

/**
 * En desarrollo se puede hablar con el motor DIRECTAMENTE (localhost:3001).
 *
 * En produccion no: el microservicio vive en subred interna detras de service
 * discovery y ningun navegador lo alcanza. Ahi se pasa por la extension de
 * Directus, que ademas persiste el resultado en `ai_suggestions`.
 *
 * OJO: en modo directo NO hay persistencia. Recargar vuelve a llamar al modelo
 * y las decisiones del usuario no se registran. Es lo aceptable para iterar en
 * local; en cuanto la extension este desplegada, se quita `VITE_IA_URL` del
 * .env.local y el desarrollo usa el mismo camino que produccion.
 */
const MOTOR_DIRECTO = import.meta.env.VITE_IA_URL as string | undefined;
export const HAY_PERSISTENCIA = !MOTOR_DIRECTO;

// Vive en errores.ts para que el nucleo del editor no dependa de este cliente.
export { ErrorApi };

const pedir = async <T>(url: string, init?: RequestInit): Promise<T> => {
  let res: Response;
  try {
    res = await fetch(url, {
      ...init,
      headers: { "Content-Type": "application/json", ...init?.headers },
    });
  } catch (causa) {
    // Sin respuesta: el usuario esta sin conexion o el backend no contesta. Se
    // distingue de un 4xx/5xx porque la UI reacciona distinto -- reintentar
    // tiene sentido aqui y no cuando el servidor ya dijo que no.
    throw new ErrorApi(0, "No hay conexión con el servidor.", causa);
  }

  const texto = await res.text();
  const cuerpo = texto ? safeJson(texto) : null;

  if (!res.ok) {
    const mensaje =
      (cuerpo as { errors?: { message?: string }[] } | null)?.errors?.[0]?.message ??
      `Error ${res.status}`;
    throw new ErrorApi(res.status, mensaje, cuerpo);
  }

  return cuerpo as T;
};

const safeJson = (texto: string): unknown => {
  try {
    return JSON.parse(texto);
  } catch {
    return texto;
  }
};

// --- lectura -----------------------------------------------------------------

/**
 * Trae un CV completo y lo devuelve ya como documento.
 *
 * `fields=*.*.*.*.*` es lo que usa hoy el builder: cinco niveles hacen falta
 * para que las relaciones M2M lleguen con sus datos y no solo con ids.
 */
export const getDocumento = async (cvId: number): Promise<CVDocument> => {
  const url = `${API}items/curriculum_vitae?fields=*.*.*.*.*&filter[id][_eq]=${cvId}`;
  const res = await pedir<{ data: unknown[] }>(url);
  if (!res.data?.length) {
    throw new ErrorApi(404, "Esa hoja de vida no existe.");
  }
  return fromDirectus(res.data);
};

// --- sugerencias -------------------------------------------------------------

/**
 * Pide las sugerencias para un documento.
 *
 * Se manda el documento entero, no el cvId: el motor no habla con Directus, y
 * asi analiza lo que el usuario tiene EN PANTALLA -- incluidos los cambios que
 * aun no se han guardado.
 */
export const generarSugerencias = async (
  doc: CVDocument,
): Promise<RespuestaSugerencias> => {
  const url = MOTOR_DIRECTO
    ? `${MOTOR_DIRECTO}/suggestions`
    : `${API}api/cv/${doc.id}/suggestions`;
  const res = await pedir<{ data: RespuestaSugerencias }>(url, {
    method: "POST",
    body: JSON.stringify({ documento: doc }),
  });
  return res.data;
};

/**
 * Las sugerencias ya guardadas, si las hay.
 *
 * Se consulta ANTES de generar: sin esto, cada recarga de la pagina vuelve a
 * pagar una llamada a Bedrock y pierde lo que el usuario ya habia decidido.
 */
export const getSugerenciasGuardadas = async (
  cvId: number,
): Promise<{ sugerencias: Sugerencia[] } | null> => {
  if (MOTOR_DIRECTO) return null; // en modo directo no hay nada guardado
  const res = await pedir<{ data: { sugerencias: Sugerencia[] } | null }>(
    `${API}api/cv/${cvId}/suggestions`,
  );
  return res.data;
};

/**
 * Registra lo que el usuario decidio sobre una sugerencia.
 *
 * Es lo que hace medible el producto: que motivos se aceptan siempre y cuales
 * se rechazan siempre -- y ese segundo grupo es un prompt que arreglar o una
 * regla que apagar. Falla en silencio a proposito: perder una metrica no puede
 * interrumpirle la revision a nadie.
 */
export const marcarSugerencia = async (
  cvId: number,
  sid: string,
  estado: Sugerencia["estado"],
  final?: string,
): Promise<void> => {
  if (MOTOR_DIRECTO) return;
  try {
    await pedir(`${API}api/cv/${cvId}/suggestions/${encodeURIComponent(sid)}`, {
      method: "PATCH",
      body: JSON.stringify({ estado, ...(final !== undefined ? { final } : {}) }),
    });
  } catch {
    /* metrica perdida, revision intacta */
  }
};

// --- entrega ------------------------------------------------------------------

/**
 * Manda la hoja de vida terminada al correo de la persona.
 *
 * No hay descarga directa: `docs-microservices` genera el PDF y el DOCX de
 * forma asincrona (SQS -> S3 -> enlace firmado) y los envia. Esta llamada solo
 * encola; que llegue depende de ese consumidor.
 */
export const enviarPorCorreo = async (cvId: number, correo: string): Promise<void> => {
  await pedir(`${API}api/send-cv-email`, {
    method: "POST",
    body: JSON.stringify({ cvId, recipientEmail: correo }),
  });
};

/**
 * Generacion directa del PDF y el Word contra `docs-microservices`.
 *
 * Solo existe cuando `VITE_DOCS_URL` esta configurado, que hoy es DESARROLLO:
 * en produccion ese servicio vive en la subred interna y el navegador no lo
 * alcanza, igual que el motor de sugerencias. Ahi la hoja se entrega por
 * correo. Para ofrecer descarga en produccion haria falta un proxy en la
 * extension de Directus, como el de sugerencias.
 */
const DOCS_DIRECTO = import.meta.env.VITE_DOCS_URL as string | undefined;
export const HAY_DESCARGA = Boolean(DOCS_DIRECTO);

export const descargarDocumento = async (
  doc: CVDocument,
  formato: "pdf" | "docx",
): Promise<void> => {
  if (!DOCS_DIRECTO) throw new ErrorApi(501, "La descarga no está disponible en este entorno.");

  const nombre =
    [doc.name, doc.last_names].filter(Boolean).join(" ").trim() || "hoja-de-vida";

  let res: Response;
  try {
    res = await fetch(`${DOCS_DIRECTO}/docs/generate?format=${formato}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ data: toCVData(doc), lang: "es", filename: nombre }),
    });
  } catch (causa) {
    throw new ErrorApi(0, "No hay conexión con el generador de documentos.", causa);
  }
  if (!res.ok) throw new ErrorApi(res.status, "No pudimos generar el documento.");

  // El archivo se entrega como descarga del navegador, sin abrir pestañas.
  const url = URL.createObjectURL(await res.blob());
  const enlace = document.createElement("a");
  enlace.href = url;
  enlace.download = `${nombre}.${formato}`;
  document.body.appendChild(enlace);
  enlace.click();
  enlace.remove();
  // Se libera en el siguiente tick: revocarlo en el acto cancela la descarga en
  // algunos navegadores.
  setTimeout(() => URL.revokeObjectURL(url), 0);
};

// --- escritura ---------------------------------------------------------------

/**
 * Guarda el documento entero en UNA peticion.
 *
 * Directus resuelve las relaciones anidadas (`create`/`update`/`delete`) dentro
 * del propio PATCH, asi que no hacen falta las doce llamadas sueltas del
 * builder viejo ni su ventana de guardado a medias.
 *
 * Provisional y con fecha de vencimiento: en F6 esto pasa a ser
 * `PUT /api/cv/:id/document`, dentro de una transaccion de verdad en el
 * servidor. Directus aplica las relaciones en secuencia, no atomicamente: si
 * falla a mitad quedan unas escritas y otras no. Con un solo usuario editando
 * su propia HV es tolerable; como contrato definitivo, no.
 */
export const guardarDocumento = async (
  cvId: number,
  payload: Record<string, unknown>,
): Promise<{ doc: CVDocument; omitidos: string[] }> => {
  const enviar = async (cuerpo: Record<string, unknown>) =>
    fromDirectus(
      (
        await pedir<{ data: unknown }>(
          `${API}items/curriculum_vitae/${cvId}?fields=*.*.*.*.*`,
          { method: "PATCH", body: JSON.stringify(cuerpo) },
        )
      ).data,
    );

  try {
    return { doc: await enviar(payload), omitidos: [] };
  } catch (e) {
    /**
     * Si Directus rechaza campos que la base no tiene, se guarda el RESTO.
     *
     * Directus trata un campo desconocido como un permiso denegado y rechaza la
     * peticion entera. Paso con `is_inclusive` y `disability_type`: el codigo
     * los mandaba, las columnas no existian en staging, y el autoguardado no
     * guardaba nada. Un campo opcional que falta no puede
     * costarle a nadie todo lo que escribio.
     */
    const rechazados = camposRechazados(e).filter((c) => c in payload);
    if (rechazados.length === 0) throw e;

    const limpio = { ...payload };
    for (const campo of rechazados) delete limpio[campo];
    return { doc: await enviar(limpio), omitidos: rechazados };
  }
};

/** Los campos que Directus dice que no existen (o no se pueden escribir). */
export const camposRechazados = (e: unknown): string[] => {
  if (!(e instanceof ErrorApi) || (e.status !== 403 && e.status !== 400)) return [];
  const tramo = e.message.match(/access fields? (.+?) in collection/i)?.[1];
  if (!tramo) return [];
  return [...tramo.matchAll(/"([^"]+)"/g)].map((m) => m[1] ?? "").filter(Boolean);
};

/**
 * El transporte de creatorhv: directo contra Directus, sin sesion.
 *
 * La suite de personas usa otro, contra su propio backend. Ver transporte.ts.
 */
export const transporteDirectus: CvTransporte = {
  guardar: guardarDocumento,
  sugerenciasGuardadas: getSugerenciasGuardadas,
  generarSugerencias,
  marcarSugerencia,
};
